<template>
  <Tag
    v-if="ticketId"
    v-tooltip="{ value: fullText }"
    class="cursor-pointer ml-auto"
    severity="info"
    :value="displayText"
    icon="mdi mdi-tag-check-outline"
    @click="handleClick"
  >
    <template #icon> <i class="mdi mdi-tag-check-outline text-xl"></i> </template
  ></Tag>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import Tag from 'primevue/tag';
import { useI18n } from 'vue-i18n';

import { useTickets } from '@/views/tickets/hooks/useTickets';
import type { TicketV1Dto } from '@/openapi';

const props = withDefaults(
  defineProps<{
    ticketId?: string | null;
    crop?: number;
  }>(),
  {
    ticketId: null,
    crop: undefined,
  },
);

const { t } = useI18n();
const { fetchTicket, goToTicketById } = useTickets();

const ticket = ref<TicketV1Dto | null>(null);
const isLoading = ref(false);

// Full text without cropping (for tooltip)
const fullText = computed(() => {
  if (isLoading.value) {
    return t('messages.loading');
  }

  if (ticket.value?.name) {
    return `[${ticket.value.number}] ${ticket.value.name}`;
  }

  return t('messages.createdByTicket');
});

// Display text with cropping logic
const displayText = computed(() => {
  if (isLoading.value) {
    return t('messages.loading');
  }

  // Crop = 0: Show only ticket number
  if (props.crop === 0 && ticket.value?.number) {
    return `[${ticket.value.number}]`;
  }

  const full = fullText.value;

  // Apply crop if defined
  if (props.crop && props.crop > 0 && full.length > props.crop) {
    return full.slice(0, props.crop) + '...';
  }

  return full;
});

const handleClick = () => {
  if (props.ticketId) {
    goToTicketById(props.ticketId);
  }
};

const loadTicket = async () => {
  if (!props.ticketId) {
    isLoading.value = false;
    ticket.value = null;
    return;
  }

  isLoading.value = true;
  try {
    const fetchedTicket = await fetchTicket(props.ticketId);
    ticket.value = fetchedTicket ?? null;
  } catch (error) {
    console.error('Failed to fetch ticket:', error);
    ticket.value = null;
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.ticketId, loadTicket, { immediate: true });
</script>
